import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { TextField } from '@mui/material';
import { Text, TextBlock } from '../../Atoms/Text';
import { CustomFieldDefinition } from '../../../types';
import {
  fetchCustomFieldDefinitionsApi,
  updateCustomFieldDefinitionApi
} from '../../../Api/Admin/CustomFields/customFieldDefinitionsApi';
import { ComponentSpinner } from '../Loading/ComponentSpinner';
import { KNOWN_CUSTOM_FIELDS } from './knownCustomFields';
import { DataTypeEditor } from './DataTypeEditor';
import { DataTypeValue, normaliseFieldType } from '../../../utils/customFieldTypes';
import { getUser, isAdmin, isCapabilityDenied, isTeamManager } from '../../../store/User';
import { CustomFieldCapability } from '../../../utils/customFieldCapabilities';
import { useChangeFieldTypeWithConfirm } from './useChangeFieldTypeWithConfirm';
import { saveFieldTypeOrChoices } from './saveFieldTypeOrChoices';

export interface ColumnNameCustomizationSectionProps {
  accountId: string;
  readOnly?: boolean;
}

export interface ColumnNameCustomizationSectionHandle {
  // Resolves false when the incompatible-values dialog is declined — callers must not report success.
  save: () => Promise<boolean>;
  isDirty: () => boolean;
}

type FieldRow = {
  definitionId: string;
  systemKey: string;
  systemLabel: string;
  name: string;
  originalName: string;
  fieldType: DataTypeValue;
  choices: string[];
  originalFieldType: DataTypeValue;
  originalChoicesKey: string;
  updatedAt: string;
  modifiedByName: string | null;
  dirty: boolean;
};

const formatDate = (dateStr: string) => {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: '2-digit'
  });
};

const choicesKey = (choices: string[]) => choices.join('|');

const buildRows = (defs: CustomFieldDefinition[]): FieldRow[] => {
  const activeRows: FieldRow[] = [];
  for (const known of KNOWN_CUSTOM_FIELDS) {
    const def = defs.find((d) => d.options?.system_key === known.systemKey && !d.is_archived);
    if (def) {
      const fieldType = normaliseFieldType(def.field_type);
      const choices = def.options?.choices ?? [];
      activeRows.push({
        definitionId: def.id,
        systemKey: known.systemKey,
        systemLabel: known.systemLabel,
        name: def.name,
        originalName: def.name,
        fieldType,
        choices,
        originalFieldType: fieldType,
        originalChoicesKey: choicesKey(choices),
        updatedAt: def.updated_at,
        modifiedByName: def.modified_by_name ?? null,
        dirty: false
      });
    }
  }
  return activeRows;
};

const userCanEdit = () => {
  const user = getUser();
  return isAdmin(user) || isTeamManager(user);
};

export const ColumnNameCustomizationSection = forwardRef<
  ColumnNameCustomizationSectionHandle,
  ColumnNameCustomizationSectionProps
>(({ accountId, readOnly }, ref) => {
  const [rows, setRows] = useState<FieldRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const effectiveReadOnly = readOnly ?? !userCanEdit();
  const updateDenied = isCapabilityDenied(CustomFieldCapability.DEFINITIONS_UPDATE, getUser());
  const nameReadOnly = effectiveReadOnly || updateDenied;
  const typeReadOnly =
    effectiveReadOnly || isCapabilityDenied(CustomFieldCapability.DEFINITIONS_CHANGE_FIELD_TYPE, getUser());
  const { change: changeFieldTypeWithConfirm, dialog: incompatibleValuesDialog } =
    useChangeFieldTypeWithConfirm('inventory');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setLoadError(null);
      const { data, error: apiError } = await fetchCustomFieldDefinitionsApi(accountId);
      if (apiError) {
        setLoadError(apiError.message);
      } else {
        setRows(buildRows(data ?? []));
      }
      setLoading(false);
    };
    load();
  }, [accountId]);

  const handleNameChange = (definitionId: string, value: string) => {
    setRows((prev) => prev.map((r) => (r.definitionId === definitionId ? { ...r, name: value, dirty: true } : r)));
  };

  const handleFieldTypeChange = (definitionId: string, value: DataTypeValue) => {
    setRows((prev) => prev.map((r) => (r.definitionId === definitionId ? { ...r, fieldType: value, dirty: true } : r)));
  };

  const handleChoicesChange = (definitionId: string, choices: string[]) => {
    setRows((prev) => prev.map((r) => (r.definitionId === definitionId ? { ...r, choices, dirty: true } : r)));
  };

  const applyRowFromDefinition = (definitionId: string, def: CustomFieldDefinition) => {
    setRows((prev) =>
      prev.map((r) =>
        r.definitionId === definitionId
          ? {
              ...r,
              fieldType: normaliseFieldType(def.field_type),
              choices: def.options?.choices ?? [],
              originalFieldType: normaliseFieldType(def.field_type),
              originalChoicesKey: choicesKey(def.options?.choices ?? []),
              name: def.name,
              originalName: def.name,
              updatedAt: def.updated_at,
              modifiedByName: def.modified_by_name ?? null,
              dirty: false
            }
          : r
      )
    );
  };

  const markClean = (definitionId: string) =>
    setRows((prev) => prev.map((r) => (r.definitionId === definitionId ? { ...r, dirty: false } : r)));

  const applyResult = (
    definitionId: string,
    data: CustomFieldDefinition | null | undefined,
    apiError: { message: string } | null | undefined
  ) => {
    if (apiError) throw new Error(apiError.message);
    if (data) applyRowFromDefinition(definitionId, data);
  };

  const saveTypeOrChoices = async (row: FieldRow): Promise<boolean> => {
    const result = await saveFieldTypeOrChoices(
      row.definitionId,
      row,
      choicesKey(row.choices) !== row.originalChoicesKey,
      changeFieldTypeWithConfirm
    );
    if (!result) return true;
    if (result.cancelled) return false;
    applyResult(row.definitionId, result.data, result.error);
    return true;
  };

  // A rename nobody made stays out of the save: a set may refuse it while still allowing the type change.
  const saveRename = async (row: FieldRow) => {
    if (row.name === row.originalName) return markClean(row.definitionId);
    const { data, error: apiError } = await updateCustomFieldDefinitionApi(row.definitionId, { name: row.name });
    applyResult(row.definitionId, data, apiError);
  };

  const handleSave = async (): Promise<boolean> => {
    for (const row of rows.filter((r) => r.dirty)) {
      if (!(await saveTypeOrChoices(row))) return false;
      await saveRename(row);
    }
    return true;
  };

  useImperativeHandle(ref, () => ({
    save: handleSave,
    isDirty: () => rows.some((r) => r.dirty)
  }));

  if (loading) return <ComponentSpinner label="Loading..." />;
  if (loadError)
    return (
      <TextBlock
        size="md"
        weight="semibold"
        className="mt-4 text-red-500">
        Failed to load column customizations: {loadError}
      </TextBlock>
    );
  if (rows.length === 0) return null;

  return (
    <>
      <TextBlock
        size="md"
        weight="semibold"
        className="mt-4">
        Column Name Customization
      </TextBlock>
      <hr className="mt-3 h-0.5 border-t-2" />

      <table className="w-full text-sm border-collapse mt-3">
        <thead>
          <tr>
            <th className="text-left py-2 pr-4 font-semibold text-gray-900 w-1/6">Column Name</th>
            <th className="text-left py-2 pr-4 font-semibold text-gray-900 w-1/4">Custom Name</th>
            <th className="text-left py-2 pr-4 font-semibold text-gray-900 w-1/4">Data Type</th>
            <th className="text-left py-2 pr-4 font-semibold text-gray-900 w-1/6">Modified by</th>
            <th className="text-left py-2 font-semibold text-gray-900 w-1/6">Modified Date</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.definitionId}
              className="align-top">
              <td className="py-2 pr-4">
                <Text
                  color="grey9"
                  size="sm">
                  {row.systemLabel}
                </Text>
              </td>
              <td className="py-2 pr-4">
                {nameReadOnly ? (
                  <Text
                    color="grey9"
                    size="sm">
                    {row.name}
                  </Text>
                ) : (
                  <TextField
                    size="small"
                    value={row.name}
                    onChange={(e) => handleNameChange(row.definitionId, e.target.value)}
                    inputProps={{ 'aria-label': `Custom name for ${row.systemLabel}` }}
                    sx={{ width: '100%' }}
                  />
                )}
              </td>
              <td className="py-2 pr-4">
                <DataTypeEditor
                  fieldType={row.fieldType}
                  choices={row.choices}
                  onFieldTypeChange={(value) => handleFieldTypeChange(row.definitionId, value)}
                  onChoicesChange={(choices) => handleChoicesChange(row.definitionId, choices)}
                  readOnly={typeReadOnly}
                  choicesReadOnly={effectiveReadOnly || (updateDenied && row.fieldType === row.originalFieldType)}
                  ariaLabel={`Data type for ${row.systemLabel}`}
                />
              </td>
              <td className="py-2 pr-4">
                <Text
                  color="grey6"
                  size="sm">
                  {row.modifiedByName ?? '—'}
                </Text>
              </td>
              <td className="py-2">
                <Text
                  color="grey6"
                  size="sm">
                  {formatDate(row.updatedAt)}
                </Text>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {incompatibleValuesDialog}
    </>
  );
});

ColumnNameCustomizationSection.displayName = 'ColumnNameCustomizationSection';
